/**
 * HomeRouter — root path dispatcher.
 *
 * Reads two signals to swap the homepage per site:
 *   1. Domain — indianfamilytree.com auto-routes to its own home page
 *   2. `?site=<slug>` query — preview any child home from inside the parent
 *
 * Use cases:
 *   /                        → default Home (or the domain-matched sibling home)
 *   /?site=kerala            → KeralaHome
 *   /?site=indianfamilytree  → IndianFamilyTreeHome
 *   /?site=socialpulse       → default Home (explicit opt-in)
 */
import { useSearchParams } from "react-router-dom";
import Home from "@/pages/Home";
import SocialPulseHome from "@/pages/SocialPulseHome";
import BlogHome from "@/pages/blog/BlogHome";
import KeralaHome from "@/pages/KeralaHome";
import KeralaTravelsHome from "@/pages/KeralaTravelsHome";
import IndianFamilyTreeHome from "@/pages/IndianFamilyTreeHome";
import MyFamilyTreeCareHome from "@/pages/MyFamilyTreeCareHome";
import FoodPulseHome from "@/pages/FoodPulseHome";
import SellnBuyHome from "@/pages/SellnBuyHome";
import SocialCapitalHome from "@/pages/SocialCapitalHome";
import WorldPulseHome from "@/pages/WorldPulseHome";
import WorldPulseLanding from "@/pages/WorldPulseLanding";
import { resolveSiteSlug } from "@/config/siteConfig";

const SITE_ROUTES = {
  // SocialPulse.com — new network-hub home (Feb 2026 redesign).
  socialpulse:       SocialPulseHome,
  kerala:            KeralaHome,
  keralatravels:     KeralaTravelsHome,
  indianfamilytree:  IndianFamilyTreeHome,
  myfamilytreecare:  MyFamilyTreeCareHome,
  foodpulse:         FoodPulseHome,
  sellnbuy:          SellnBuyHome,
  socialcapital:     SocialCapitalHome,
  worldpulse:        WorldPulseLanding,
  indias:            WorldPulseHome,
  usapulse:          WorldPulseHome,
  // BlogsPulse — SocialPulse.blog editorial landing.
  blogspulse:        BlogHome,
};

export default function HomeRouter() {
  const [params] = useSearchParams();
  const queryParam = (params.get("site") || "").toLowerCase();
  const site = queryParam || resolveSiteSlug();
  // Default: on socialpulse.com apex (or any unrecognised site) show the
  // new SocialPulseHome so the legacy Home.jsx never surfaces again.
  const Component = SITE_ROUTES[site] || SocialPulseHome;
  return <Component />;
}
